// frontend/src/pages/Admin/EmailQueue.tsx
// Dashboard de la file d'attente emails — SmartLawyer

import { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from 'pages/Page';
import { ChainlitContext, useAuth } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

// ── Types ─────────────────────────────────────────────────────────────────────

interface EmailItem {
  email_id:     string;
  to_email:     string;
  to_name?:     string;
  subject:      string;
  category:     'prospection' | 'broadcast' | 'system';
  status:       'pending' | 'sending' | 'sent' | 'failed' | 'cancelled';
  priority:     number;
  created_at:   string;
  sent_at?:     string;
  failed_at?:   string;
  error?:       string;
  retry_count:  number;
  from_email:   string;
  from_name?:   string;
  html_body?:   string;
}

interface Stats {
  total:        number;
  by_status:    { pending: number; sending: number; sent: number; failed: number; cancelled: number };
  by_category:  { prospection: number; broadcast: number; system: number };
  today_sent:   number;
  today_failed: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch { return iso; }
};

const StatusBadge = ({ status }: { status: EmailItem['status'] }) => {
  const cfg: Record<EmailItem['status'], { cls: string; label: string }> = {
    pending:   { cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',   label: 'En attente' },
    sending:   { cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',           label: 'Envoi…' },
    sent:      { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', label: 'Envoyé' },
    failed:    { cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',           label: 'Échoué' },
    cancelled: { cls: 'bg-muted text-muted-foreground',                                              label: 'Annulé' },
  };
  const { cls, label } = cfg[status] || cfg.cancelled;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>;
};

const CategoryBadge = ({ category }: { category: EmailItem['category'] }) => {
  const cfg: Record<EmailItem['category'], { cls: string; label: string }> = {
    prospection: { cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', label: 'Prospection' },
    broadcast:   { cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',         label: 'Broadcast' },
    system:      { cls: 'bg-muted text-muted-foreground',                                            label: 'Système' },
  };
  const { cls, label } = cfg[category] || cfg.system;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>;
};

const StatCard = ({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) => (
  <div className="bg-card border border-border rounded-lg p-4 flex flex-col gap-1">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={`text-2xl font-bold ${color || 'text-foreground'}`}>{value}</span>
    {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
  </div>
);

// ── Composant principal ───────────────────────────────────────────────────────

const EmailQueueContent = () => {
  const { isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate  = useNavigate();

  const [stats,        setStats]        = useState<Stats | null>(null);
  const [emails,       setEmails]       = useState<EmailItem[]>([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [loading,      setLoading]      = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error,        setError]        = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Filtres
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSearch,   setFilterSearch]   = useState('');
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (!isReady) return;
    if (!isAuthenticated) navigate('/login');
  }, [isAuthenticated, isReady, navigate]);

  // Chargement stats
  const loadStats = useCallback(() => {
    if (!isAuthenticated || !isReady) return;
    setLoadingStats(true);
    fetch(apiClient.buildEndpoint('/api/email-queue/stats'))
      .then(r => r.json())
      .then(d => { setStats(d); setLoadingStats(false); })
      .catch(() => setLoadingStats(false));
  }, [isAuthenticated, isReady, apiClient]);

  useEffect(() => { loadStats(); }, [loadStats]);

  // Chargement liste
  const loadEmails = useCallback(() => {
    if (!isAuthenticated || !isReady) return;
    setLoading(true);
    setError('');

    const params = new URLSearchParams({
      limit:  String(PAGE_SIZE),
      offset: String((page - 1) * PAGE_SIZE),
    });
    if (filterStatus)   params.set('status',   filterStatus);
    if (filterCategory) params.set('category', filterCategory);
    if (filterSearch)   params.set('search',   filterSearch);

    fetch(apiClient.buildEndpoint(`/api/email-queue/?${params}`))
      .then(r => { if (!r.ok) throw new Error('Erreur API'); return r.json(); })
      .then(d => {
        setEmails(d.items ?? []);
        setTotal(d.total ?? 0);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [isAuthenticated, isReady, page, filterStatus, filterCategory, filterSearch, apiClient]);

  useEffect(() => { loadEmails(); }, [loadEmails]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Actions
  const handleCancel = async (email_id: string) => {
    setActionLoading(email_id);
    try {
      const resp = await fetch(
        apiClient.buildEndpoint(`/api/email-queue/${email_id}/cancel`),
        { method: 'POST' }
      );
      if (resp.ok) {
        loadEmails();
        loadStats();
      } else {
        const d = await resp.json();
        alert(d.detail || 'Erreur lors de l\'annulation');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetry = async (email_id: string) => {
    setActionLoading(email_id);
    try {
      const resp = await fetch(
        apiClient.buildEndpoint(`/api/email-queue/${email_id}/retry`),
        { method: 'POST' }
      );
      if (resp.ok) {
        loadEmails();
        loadStats();
      } else {
        const d = await resp.json();
        alert(d.detail || 'Erreur lors de la remise en file');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (email_id: string) => {
    if (!confirm('Supprimer définitivement cet email ?')) return;
    setActionLoading(email_id);
    try {
      const resp = await fetch(
        apiClient.buildEndpoint(`/api/email-queue/${email_id}`),
        { method: 'DELETE' }
      );
      if (resp.ok) {
        loadEmails();
        loadStats();
      }
    } finally {
      setActionLoading(null);
    }
  };

  const FilterSelect = ({ label, value, onChange, options }: {
    label: string; value: string; onChange: (v: string) => void;
    options: { value: string; label: string }[];
  }) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <select
        value={value}
        onChange={e => { onChange(e.target.value); setPage(1); }}
        className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  return (
    <div className="flex flex-col gap-6 p-6 max-w-screen-xl mx-auto w-full h-full overflow-y-auto [&>*]:shrink-0">
      {/* Titre */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">File d'attente emails</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Gestion des emails en attente, envoyés et échoués — prospection, broadcast, système
        </p>
      </div>

      {/* Stats */}
      {loadingStats
        ? <div className="flex gap-2"><Loader /></div>
        : stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Total"
              value={stats.total}
            />
            <StatCard
              label="En attente"
              value={stats.by_status.pending + stats.by_status.sending}
              sub={`${stats.by_status.pending} pending · ${stats.by_status.sending} en cours`}
              color="text-yellow-600"
            />
            <StatCard
              label="Envoyés aujourd'hui"
              value={stats.today_sent}
              sub={`Total : ${stats.by_status.sent}`}
              color="text-emerald-600"
            />
            <StatCard
              label="Échoués aujourd'hui"
              value={stats.today_failed}
              sub={`Total : ${stats.by_status.failed} · Annulés : ${stats.by_status.cancelled}`}
              color={stats.today_failed > 0 ? 'text-rose-600' : 'text-foreground'}
            />
          </div>
        )}

      {/* Filtres */}
      <div className="bg-card border border-border rounded-lg p-4 flex flex-wrap gap-4 items-end">
        <FilterSelect
          label="Statut"
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: '', label: 'Tous les statuts' },
            { value: 'pending',   label: 'En attente' },
            { value: 'sending',   label: 'En cours' },
            { value: 'sent',      label: 'Envoyé' },
            { value: 'failed',    label: 'Échoué' },
            { value: 'cancelled', label: 'Annulé' },
          ]}
        />
        <FilterSelect
          label="Catégorie"
          value={filterCategory}
          onChange={setFilterCategory}
          options={[
            { value: '', label: 'Toutes' },
            { value: 'prospection', label: 'Prospection' },
            { value: 'broadcast',   label: 'Broadcast' },
            { value: 'system',      label: 'Système' },
          ]}
        />
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">Recherche email / sujet</label>
          <input
            type="text"
            value={filterSearch}
            onChange={e => { setFilterSearch(e.target.value); setPage(1); }}
            placeholder="avocat@cabinet.fr ou mot-clé…"
            className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <button
          onClick={() => { setFilterStatus(''); setFilterCategory(''); setFilterSearch(''); setPage(1); }}
          className="text-xs text-muted-foreground hover:text-foreground px-3 py-1.5 border border-border rounded-lg"
        >
          Réinitialiser
        </button>
        <button
          onClick={() => { loadEmails(); loadStats(); }}
          className="text-xs px-3 py-1.5 border border-border rounded-lg hover:bg-muted"
        >
          Actualiser
        </button>
      </div>

      {/* Tableau */}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {loading
        ? <div className="flex justify-center py-12"><Loader /></div>
        : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border text-sm text-muted-foreground">
              {total} email{total > 1 ? 's' : ''} · page {page}/{totalPages || 1}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Destinataire</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Sujet</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground">Catégorie</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground">Statut</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground">Prio</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Créé le</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Envoyé / Échoué</th>
                    <th className="px-3 py-3 text-xs font-semibold text-muted-foreground text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map(email => {
                    const isLoading = actionLoading === email.email_id;
                    return (
                      <tr key={email.email_id} className="border-b border-border hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 max-w-[180px]">
                          <div className="font-medium text-foreground truncate">{email.to_email}</div>
                          {email.to_name && (
                            <div className="text-xs text-muted-foreground truncate">{email.to_name}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-[220px]">
                          <div className="truncate text-foreground" title={email.subject}>{email.subject}</div>
                          {email.error && (
                            <div className="text-xs text-rose-600 truncate mt-0.5" title={email.error}>
                              {email.error}
                            </div>
                          )}
                          {email.retry_count > 0 && (
                            <div className="text-xs text-muted-foreground">Tentative #{email.retry_count}</div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <CategoryBadge category={email.category} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <StatusBadge status={email.status} />
                        </td>
                        <td className="px-3 py-3 text-center text-muted-foreground text-xs">
                          {email.priority}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                          {fmtDateTime(email.created_at)}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                          {email.status === 'sent' && fmtDateTime(email.sent_at)}
                          {email.status === 'failed' && (
                            <span className="text-rose-600">{fmtDateTime(email.failed_at)}</span>
                          )}
                          {!['sent', 'failed'].includes(email.status) && '—'}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {email.status === 'pending' && (
                              <button
                                onClick={() => handleCancel(email.email_id)}
                                disabled={isLoading}
                                className="text-xs px-2 py-1 rounded border border-yellow-300 text-yellow-700 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 disabled:opacity-40"
                              >
                                Annuler
                              </button>
                            )}
                            {(email.status === 'failed' || email.status === 'cancelled') && (
                              <button
                                onClick={() => handleRetry(email.email_id)}
                                disabled={isLoading}
                                className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-40"
                              >
                                Retenter
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(email.email_id)}
                              disabled={isLoading}
                              className="text-xs px-2 py-1 rounded border border-rose-300 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-40"
                            >
                              {isLoading ? '…' : 'Suppr.'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {emails.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-12 text-muted-foreground">
                        Aucun email correspondant aux filtres.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 p-4 border-t border-border">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="text-sm px-3 py-1.5 rounded border border-border disabled:opacity-40 hover:bg-muted"
                >
                  Précédent
                </button>
                <span className="text-sm text-muted-foreground">
                  Page {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="text-sm px-3 py-1.5 rounded border border-border disabled:opacity-40 hover:bg-muted"
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        )}
    </div>
  );
};

// ── Export ────────────────────────────────────────────────────────────────────

const EmailQueue = () => (
  <Page>
    <EmailQueueContent />
  </Page>
);

export default EmailQueue;
